/**
 * Native Video Player Component
 * A reusable web component for native video playback with custom controls
 */
class NativeVideoPlayer extends HTMLElement {
    connectedCallback() {
        this.video = this.querySelector("video");
        this.playPauseBtn = this.querySelector("[data-play-pause-btn]");
        this.playIcon = this.querySelector("[data-play-icon]");
        this.pauseIcon = this.querySelector("[data-pause-icon]");
        this.soundToggleBtn = this.querySelector("[data-sound-toggle-btn]");
        this.soundOffIcon = this.querySelector("[data-sound-off-icon]");
        this.soundOnIcon = this.querySelector("[data-sound-on-icon]");
        console.log("NativeVideoPlayer connected", this);

        if (!this.video) {
            console.warn("NativeVideoPlayer: No video element found");
            return;
        }

        // Get configuration from data attributes
        this.config = {
            showControls: this.dataset.showControls === "true",
            autoplay: this.dataset.autoplay === "true",
            loop: this.dataset.loop === "true",
            muted: this.dataset.muted === "true",
        };

        this.setupVideo();
        this.setupEventListeners();

        // Handle autoplay if enabled
        if (this.config.autoplay) {
            this.handleAutoplay();
        }
    }

    disconnectedCallback() {
        if (this.video) {
            this.video.pause();
        }
    }

    setupVideo() {
        this.video.loop = this.config.loop;
        this.video.muted = this.config.muted;
        this.video.playsInline = true; // Important for mobile

        if (this.config.showControls) {
            this.video.controls = true;
        } else {
            this.video.controls = false;
        }
    }

    hideElement(element) {
        if (!element) return;
        element.classList.add("is-hidden");
    }

    showElement(element, display = "") {
        if (!element) return;
        element.classList.remove("is-hidden");

        if (display) {
            element.style.display = display;
        } else {
            element.style.removeProperty("display");
        }
    }

    setupEventListeners() {
        if (this.playPauseBtn && !this.config.showControls) {
            this.playPauseBtn.addEventListener("click", (event) => {
                // Prevent parent links/containers from hijacking the control click.
                event.preventDefault();
                event.stopPropagation();
                this.togglePlayPause();
            });
        }

        if (this.soundToggleBtn && !this.config.showControls) {
            this.soundToggleBtn.addEventListener("click", (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.toggleSound();
            });
        }

        if (!this.config.showControls) {
            this.video.addEventListener("click", (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.togglePlayPause();
            });
        }

        this.video.addEventListener("play", () => {
            this.onPlay();
        });

        this.video.addEventListener("pause", () => {
            this.onPause();
        });

        this.video.addEventListener("ended", () => {
            this.onEnded();
        });

        this.video.addEventListener("loadeddata", () => {
            this.classList.add("loaded");
        });

        // Error handling
        this.video.addEventListener("error", (e) => {
            console.error("Video playback error:", e);
            this.classList.add("error");
        });
    }

    togglePlayPause() {
        if (this.video.paused) {
            this.playVideo();
        } else {
            this.pauseVideo();
        }
    }

    async playVideo() {
        try {
            await this.video.play();
        } catch (error) {
            console.warn("Video play failed:", error);
        }
    }

    pauseVideo() {
        this.video.pause();
    }

    onPlay() {
        this.classList.add("playing");
        this.classList.remove("paused");

        // Update play/pause icon
        if (this.playIcon && this.pauseIcon && !this.config.showControls) {
            this.hideElement(this.playIcon);
            this.showElement(this.pauseIcon, "flex");
        }

        // Show sound toggle button when playing
        if (this.soundToggleBtn && !this.config.showControls) {
            this.showElement(this.soundToggleBtn, "flex");
        }

        this.dispatchEvent(
            new CustomEvent("video:play", {
                bubbles: true,
                detail: { video: this.video },
            })
        );
    }

    onPause() {
        this.classList.remove("playing");
        this.classList.add("paused");

        // Update play/pause icon
        if (this.playIcon && this.pauseIcon && !this.config.showControls) {
            this.showElement(this.playIcon, "flex");
            this.hideElement(this.pauseIcon);
        }

        // Hide sound toggle button when paused
        if (this.soundToggleBtn && !this.config.showControls) {
            this.hideElement(this.soundToggleBtn);
        }

        this.dispatchEvent(
            new CustomEvent("video:pause", {
                bubbles: true,
                detail: { video: this.video },
            })
        );
    }

    onEnded() {
        this.classList.remove("playing");
        this.classList.add("ended");

        // Update play/pause icon (if not looping)
        if (
            this.playIcon &&
            this.pauseIcon &&
            !this.config.loop &&
            !this.config.showControls
        ) {
            this.showElement(this.playIcon, "flex");
            this.hideElement(this.pauseIcon);
        }

        // Hide sound toggle button when ended
        if (this.soundToggleBtn && !this.config.showControls) {
            this.hideElement(this.soundToggleBtn);
        }

        this.dispatchEvent(
            new CustomEvent("video:ended", {
                bubbles: true,
                detail: { video: this.video },
            })
        );
    }

    toggleSound() {
        if (this.video.muted) {
            this.video.muted = false;
            this.hideElement(this.soundOffIcon);
            this.showElement(this.soundOnIcon, "flex");
        } else {
            this.video.muted = true;
            this.showElement(this.soundOffIcon, "flex");
            this.hideElement(this.soundOnIcon);
        }
    }

    async handleAutoplay() {
        if (!this.config.muted) {
            console.warn(
                "NativeVideoPlayer: Autoplay requires muted video in most browsers"
            );
            this.video.muted = true;
        }

        // Wait a bit for video to be ready
        await new Promise((resolve) => setTimeout(resolve, 100));

        try {
            await this.video.play();
        } catch (error) {
            console.warn("Autoplay failed:", error);

            if (this.playIcon) {
                this.showElement(this.playIcon, "flex");
            }
        }
    }
}

if (!customElements.get("native-video-player")) {
    customElements.define("native-video-player", NativeVideoPlayer);
}
